/**
 * EVENTSFLO-ADMIN-INBOX-CREATE-VENUE-FROM-REQUEST-V1
 *
 * Admin resolution panel for free-text venue requests. Rendered under
 * the `Client requested new venue` badge on the Admin Client Requests
 * detail panel.
 *
 * Three actions:
 *   1. Create venue from request   — opens a pre-filled create dialog;
 *      handles 409 duplicate response with a Link/Create-anyway choice.
 *   2. Link existing venue         — opens an existing-venue selector.
 *   3. Leave as free text          — dismisses the panel for this session
 *      (no backend mutation — original typed text is preserved).
 *
 * Backend: POST /api/admin/project-requests/{id}/resolve-venue
 *   create body: { mode:"create", venue_name, venue_address?, confirm_duplicate? }
 *   link  body: { mode:"link",   venue_id }
 */
import { useState, useEffect, useMemo } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { AlertTriangle, MapPin, Link as LinkIcon, X } from "lucide-react";

export function RequestedVenueResolutionPanel({ request, onResolved }) {
  const [dismissed, setDismissed] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [name, setName] = useState(request.venue || "");
  const [address, setAddress] = useState(request.venue_address || "");
  const [submitting, setSubmitting] = useState(false);
  const [duplicate, setDuplicate] = useState(null); // { venue_id, venue_name, address?, city? }
  const [venues, setVenues] = useState([]);
  const [linkVenueId, setLinkVenueId] = useState("");

  // Reset transient state when the request changes (admin opens a different one).
  useEffect(() => {
    setName(request.venue || "");
    setAddress(request.venue_address || "");
    setDuplicate(null);
    setLinkVenueId("");
  }, [request.id, request.venue, request.venue_address]);

  // Fetch the tenant's venues for the link dialog only when it opens.
  useEffect(() => {
    if (!linkOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/venues");
        if (!cancelled) {
          const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
          // Show venues that match the client or are unowned/shared with it.
          const filtered = list.filter(
            (v) =>
              !v.client_id ||
              v.client_id === request.client_id ||
              (v.shared_client_ids || []).includes(request.client_id),
          );
          setVenues(filtered);
        }
      } catch (err) {
        if (!cancelled) toast.error("Failed to load venues");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkOpen, request.client_id]);

  const submitCreate = async (confirmDuplicate = false) => {
    const trimmedName = (name || "").trim();
    if (!trimmedName) {
      toast.error("Venue name is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/admin/project-requests/${request.id}/resolve-venue`,
        {
          mode: "create",
          venue_name: trimmedName,
          venue_address: (address || "").trim() || undefined,
          confirm_duplicate: !!confirmDuplicate,
        },
      );
      toast.success("Venue created and linked to request");
      setCreateOpen(false);
      setDuplicate(null);
      if (typeof onResolved === "function") await onResolved(res.data);
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      if (status === 409 && body?.error === "duplicate_venue") {
        setDuplicate(body.match);
        return;
      }
      toast.error(
        body?.detail || err?.message || "Failed to resolve venue",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitLink = async () => {
    if (!linkVenueId) {
      toast.error("Pick a venue to link");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/admin/project-requests/${request.id}/resolve-venue`,
        { mode: "link", venue_id: linkVenueId },
      );
      toast.success("Request linked to existing venue");
      setLinkOpen(false);
      if (typeof onResolved === "function") await onResolved(res.data);
    } catch (err) {
      const body = err?.response?.data;
      toast.error(
        body?.detail || err?.message || "Failed to link venue",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (dismissed) return null;

  return (
    <div
      className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-3"
      data-testid="requested-venue-resolution-panel"
    >
      <p className="text-xs text-amber-100 mb-2 flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5" />
        Resolve client&#39;s requested venue: <span className="font-semibold">{request.venue}</span>
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          data-testid="requested-venue-create-btn"
        >
          <MapPin className="h-3.5 w-3.5 mr-1" />
          Create venue from request
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setLinkOpen(true)}
          data-testid="requested-venue-link-btn"
        >
          <LinkIcon className="h-3.5 w-3.5 mr-1" />
          Link existing venue
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setDismissed(true)}
          data-testid="requested-venue-leave-btn"
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Leave as free text
        </Button>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="requested-venue-create-dialog">
          <DialogHeader>
            <DialogTitle>Create venue from request</DialogTitle>
            <DialogDescription>
              Review and confirm the details below. The new venue will be
              attached to the client and linked to this request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Venue name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                data-testid="requested-venue-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Address (optional)</Label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                maxLength={500}
                data-testid="requested-venue-address-input"
              />
            </div>
            {duplicate ? (
              <div
                className="rounded border border-amber-500/40 bg-amber-500/10 p-3"
                data-testid="requested-venue-duplicate-warning"
              >
                <p className="text-xs text-amber-200 flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  A venue with this name already exists for this client.
                </p>
                <p className="text-xs text-amber-100/80 mt-1">
                  Match: <span className="font-medium">{duplicate.venue_name}</span>
                  {duplicate.address ? ` — ${duplicate.address}` : ""}
                  {duplicate.city ? `, ${duplicate.city}` : ""}
                </p>
                <p className="text-[11px] text-amber-100/70 mt-1">
                  We recommend linking to the existing match rather than
                  creating a duplicate.
                </p>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            {duplicate ? (
              <>
                <Button
                  variant="outline"
                  onClick={async () => {
                    // Pre-load venues + pre-select the duplicate so it
                    // shows immediately when the link dialog opens.
                    setLinkVenueId(duplicate.venue_id);
                    try {
                      const res = await api.get("/venues");
                      const list = Array.isArray(res.data)
                        ? res.data
                        : res.data?.data || [];
                      setVenues(
                        list.filter(
                          (v) =>
                            !v.client_id ||
                            v.client_id === request.client_id ||
                            (v.shared_client_ids || []).includes(request.client_id),
                        ),
                      );
                    } catch (_) {
                      /* link dialog effect will retry */
                    }
                    setCreateOpen(false);
                    setLinkOpen(true);
                  }}
                  data-testid="requested-venue-link-from-duplicate-btn"
                >
                  Link to existing
                </Button>
                <Button
                  onClick={() => submitCreate(true)}
                  disabled={submitting}
                  data-testid="requested-venue-create-anyway-btn"
                >
                  Create anyway
                </Button>
              </>
            ) : (
              <Button
                onClick={() => submitCreate(false)}
                disabled={submitting}
                data-testid="requested-venue-create-submit-btn"
              >
                Create &amp; link
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent data-testid="requested-venue-link-dialog">
          <DialogHeader>
            <DialogTitle>Link to an existing venue</DialogTitle>
            <DialogDescription>
              Pick the venue that matches what the client typed.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Existing venues</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              value={linkVenueId}
              onChange={(e) => setLinkVenueId(e.target.value)}
              data-testid="requested-venue-existing-select"
            >
              <option value="">— Select a venue —</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                  {v.address ? ` — ${v.address}` : ""}
                </option>
              ))}
            </select>
            {venues.length === 0 && (
              <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1 flex-wrap">
                <span>No venues are available for this client. Use</span>
                <Badge variant="outline">Create venue from request</Badge>
                <span>instead.</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitLink}
              disabled={submitting || !linkVenueId}
              data-testid="requested-venue-link-submit-btn"
            >
              Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RequestedVenueResolutionPanel;
